import { Button, Col, Row, Select, Slider, Spin, Typography, message } from "antd"
import React, { useContext, useState, useEffect } from "react"
import "./AudioChange.less"
import { GET_MUSICS } from "../../../../rest-api/queries/music"
import { API_URL } from "../../../../utils/config"
import { UPDATE_USER_MUSIC } from "../../../../rest-api/mutations/userMusic"
import { AuthActions, AuthContext } from "../../../../contexts/auth/AuthContext"
import { useMutation, useQuery } from "@tanstack/react-query"
import { SoundOutlined, CaretRightOutlined } from "@ant-design/icons"
import { soundEngine } from "../../../../utils/soundEngine"
import i18n from "../../../../services/i18n"

const { Text } = Typography

export const PRESET_SOUNDS = [
  { id: 1, name: "Telegram Chime (Классический)" },
  { id: 2, name: "iPhone Note (Колокольчики)" },
  { id: 3, name: "WhatsApp Pop (Мягкий клик)" },
  { id: 4, name: "Crystal Drop (Хрустальная капля)" },
  { id: 5, name: "Modern Ping (Деловой сигнал)" },
  { id: 6, name: "Success Harmony (Мажорный аккорд)" },
  { id: 7, name: "Marimba Echo (Маримба)" },
  { id: 8, name: "Subtle Chime (Легкий перезвон)" },
  { id: 9, name: "Bubble Droplet (Водный пузырек)" },
  { id: 10, name: "Gentle Alert (Мягкое напоминание)" },
  // Boshliq (2026-09-03): «yana 10 tacha yoqimliroq ovoz qo'shib qo'y»
  { id: 11, name: "Soft Rain (Мягкий дождь)" },
  { id: 12, name: "Warm Wood (Тёплое дерево)" },
  { id: 13, name: "Calm Bell (Спокойный колокол)" },
  { id: 14, name: "Morning Dew (Утренняя роса)" },
  { id: 15, name: "Soft Knock (Тихий стук)" },
  { id: 16, name: "Glass Touch (Стекло)" },
  { id: 17, name: "Low Pulse (Низкий импульс)" },
  { id: 18, name: "Kalimba (Калимба)" },
  { id: 19, name: "Night Chime (Ночной перезвон)" },
  { id: 20, name: "Quick Tick (Короткий тик)" },
]

export default function AudioChange() {
  const { authState, authDispatch } = useContext(AuthContext)
  // Boshliq (2026-09-04): eng yangi manba — localStorage (tanlov shu yerga darhol yoziladi), keyin state
  const initialMusicId = (() => {
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "{}")?.musicId
      if (stored !== undefined && stored !== null) return stored
    } catch {}
    return authState?.user?.musicId ?? 5   // Boshliq (2026-09-05): standart Modern Ping
  })()

  const [selected, setSelected] = useState<number>(initialMusicId === undefined ? 5 : initialMusicId)

  // Boshliq (2026-09-03): «Notificationni ovozini pasroq qilish imkoni bormi?»
  // Balandlik brauzerda saqlanadi (serverga bog'liq emas) va barcha ovozlarga
  // birdan qo'llanadi — soundEngine ichidagi yagona master tugun orqali.
  const [volume, setVolume] = useState<number>(() => Math.round(soundEngine.getVolume() * 100))

  const { data: musics, isLoading: loading } = useQuery(
    ["GET_MUSICS"],
    GET_MUSICS,
    { retry: false }
  )

  const { mutate: updateUserMusic, isLoading: mLoading } = useMutation(
    UPDATE_USER_MUSIC,
    {
      onSuccess: () => {
        message.success(i18n.t("soundSaved"))
      },
      onError: () => {
        message.error(i18n.t("soundSaveError"))
      },
    }
  )

  // Boshliq (2026-09-04): state o'zgarsa ro'yxat unga moslashadi, LEKIN localStorage'dagi (eng yangi) tanlov ustun —
  // aks holda «ovozsiz» tanlangandan keyin eski qiymat qaytib, tanlov o'zgarmagandek ko'rinardi
  useEffect(() => {
    let stored: any
    try { stored = JSON.parse(localStorage.getItem("user") || "{}")?.musicId } catch {}
    const next = stored !== undefined && stored !== null ? stored : authState?.user?.musicId
    if (next !== undefined && next !== null) setSelected(next)
  }, [authState?.user?.musicId])

  const handlePlayPreview = (musicId: number) => {
    if (musicId === 0) return
    soundEngine.playSound(musicId)
  }

  const onChange = (musicId: number) => {
    setSelected(musicId)
    updateUserMusic({ musicID: musicId })
    // Boshliq (2026-09-04): state ham darhol yangilanadi (localStorage'ni AuthContext yozadi) —
    // avval faqat localStorage yozilardi va keyingi huquq-yangilanish eski qiymatni qaytarardi
    authDispatch({ type: AuthActions.SET_MUSIC, payload: { musicId } as any })

    // Automatically play preview
    if (musicId > 0) {
      handlePlayPreview(musicId)
    }
  }

  // Combine server musics or preset fallback
  const soundList =
    musics && musics.length > 0
      ? PRESET_SOUNDS.map((preset) => {
          const serverMatch = musics.find((m) => m.id === preset.id)
          return {
            id: preset.id,
            name: preset.name,
            musicUrl: serverMatch?.musicUrl,
          }
        })
      : PRESET_SOUNDS

  const options = [
    {
      label: i18n.t("soundOff"),
      value: 0,
    },
    ...soundList.map((sound) => ({
      label: `🔔 ${sound.name}`,
      value: sound.id,
    })),
  ]

  // Ovozlar Web Audio bilan SINTEZ qilinadi — serverda fayl bo'lishi shart emas.
  // Shuning uchun <audio> pleyerini faqat HAQIQATAN fayl bor bo'lganda ko'rsatamiz,
  // aks holda yangi ovozlarda (11-20) buzuq pleyer chiqardi.
  const serverFayl = (soundList.find((x: any) => x.id === selected) as any)?.musicUrl

  const baseUrl = API_URL.endsWith("/") ? API_URL : `${API_URL}/`
  const audioStreamUrl = `${baseUrl}Musics/audio/${selected}`

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: 8 }}>
      <Row
        gutter={[16, 16]}
        align="middle"
        justify="space-between"
        style={{
          padding: "16px 20px",
          background: "#f8fafc",
          borderRadius: 10,
          border: "1px solid #e2e8f0",
        }}
      >
        <Col xs={24} md={14}>
          <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8, color: "#1e293b", marginBottom: 4 }}>
            <SoundOutlined style={{ color: "#2563eb", fontSize: "18px" }} />
            <span style={{ fontSize: "15px" }}>{i18n.t("notifSoundPick")}</span>
          </div>
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: 10 }}>
            Выберите звук для входящих сообщений в чате, новых заказов и оповещений
          </Text>

          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Select
              style={{ flex: 1 }}
              size="middle"
              value={selected}
              onChange={onChange}
              loading={loading || mLoading}
              options={options}
            />
            {selected > 0 && (
              <Button
                type="primary"
                icon={<CaretRightOutlined />}
                onClick={() => handlePlayPreview(selected)}
                style={{
                  background: "#2563eb",
                  borderColor: "#2563eb",
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 500,
                }}
              >
                Прослушать
              </Button>
            )}
          </div>

          {/* Boshliq: ovoz balandligi */}
          {selected > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                <Text style={{ fontSize: 13, color: "#334155" }}>{i18n.t("notifVolume")}</Text>
                <Text strong style={{ fontSize: 13, color: "#2563eb" }}>{volume}%</Text>
              </div>
              <Slider
                min={0}
                max={100}
                step={5}
                value={volume}
                tooltipVisible={false}   // antd v4 (v5 dagi `tooltip` emas)
                onChange={(v: number) => { setVolume(v); soundEngine.setVolume(v) }}
                onAfterChange={(v: number) => { soundEngine.setVolume(v); if (selected > 0) handlePlayPreview(selected) }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>{i18n.t("notifVolumeHint")}</Text>
            </div>
          )}
        </Col>

        <Col xs={24} md={10} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <Spin spinning={loading || mLoading}>
            {selected > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                {serverFayl ? (
                  <audio
                    controls
                    key={`audio_${selected}`}
                    src={audioStreamUrl}
                    style={{ width: "100%", maxWidth: "260px", height: "34px" }}
                  />
                ) : null}
                <span style={{ fontSize: "11px", color: "#64748b" }}>
                  {i18n.t("notifPreviewHint")}
                </span>
              </div>
            ) : (
              <div
                style={{
                  color: "#94a3b8",
                  fontSize: "13px",
                  fontStyle: "italic",
                  background: "#f1f5f9",
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "1px dashed #cbd5e1",
                }}
              >
                🔕 Звуковое оповещение отключено
              </div>
            )}
          </Spin>
        </Col>
      </Row>
    </div>
  )
}
